import { useEffect, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ListPlus, Play, Trash2, UserRound } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Cover, IconButton, SpotifyBadge } from '@/components/music';
import type { SongInput } from './MusicPlaylists';

/**
 * Verlauf tab on the Music page (web-api routes/history.ts): recently played
 * (▶ = queue it again), top songs and top listeners for 7 / 30 days / all.
 * The bot records every song that played (Music/functions/history.js), the
 * radio not. Seeing needs the Music view right; playing and deleting edit.
 */

interface PlayedSong {
  songKey: string;
  title: string;
  artist: string | null;
  url: string;
  thumbnail: string | null;
  durationSec: number | null;
  source: 'youtube' | 'soundcloud' | 'spotify';
}

interface Play extends PlayedSong {
  id: string;
  userId: string | null;
  userName: string | null;
  userAvatar: string | null;
  listenedSec: number;
  playedAt: string;
}

type Range = '7d' | '30d' | 'all';

interface Top {
  range: Range;
  totals: { plays: number; listenedSec: number };
  songs: (PlayedSong & { plays: number })[];
  listeners: { userId: string; userName: string | null; userAvatar: string | null; requests: number; listenedSec: number }[];
}

const RANGES: { key: Range; label: string }[] = [
  { key: '7d', label: '7 Tage' },
  { key: '30d', label: '30 Tage' },
  { key: 'all', label: 'Alles' },
];

const historyKey = (guildId: string) => ['music-history', guildId];

function listenTime(sec: number) {
  if (sec < 3600) return `${Math.max(0, Math.round(sec / 60))} Min.`;
  return `${(sec / 3600).toFixed(1).replace('.', ',')} Std.`;
}

function dayLabel(date: Date) {
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(date, today)) return 'Heute';
  if (same(date, yesterday)) return 'Gestern';
  return date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short', year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}

const clock = (date: Date) => date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

const toSongInput = (s: PlayedSong): SongInput => ({
  title: s.title,
  artist: s.artist,
  url: s.url,
  durationSec: s.durationSec,
  thumbnail: s.thumbnail,
  source: s.source,
  spotify: s.source === 'spotify' ? { title: s.title, artists: s.artist, durationSec: s.durationSec } : null,
});

export function HistoryTab({
  guildId,
  canEdit,
  canPlay,
  playHint,
  currentUid,
  onPlay,
  onAddToPlaylist,
}: {
  guildId: string;
  canEdit: boolean;
  canPlay: boolean;
  /** Why ▶ is off (no edit right / no voice channel). */
  playHint: string | null;
  /** The song that plays now — a new one shows up here shortly after it starts. */
  currentUid: string | null;
  onPlay: (url: string) => void;
  onAddToPlaylist: (song: SongInput) => void;
}) {
  const qc = useQueryClient();
  const base = `/api/guilds/${guildId}/music/history`;
  const [range, setRange] = useState<Range>('7d');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const recent = useInfiniteQuery({
    queryKey: [...historyKey(guildId), 'recent'],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      api<{ plays: Play[]; nextCursor: string | null }>(`${base}?limit=20${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const top = useQuery({
    queryKey: [...historyKey(guildId), 'top', range],
    queryFn: () => api<Top>(`${base}/top?range=${range}`),
  });
  // The bot writes the row once the song is audible (~1–2 s after the switch).
  useEffect(() => {
    if (!currentUid) return;
    const t = setTimeout(() => qc.invalidateQueries({ queryKey: historyKey(guildId) }), 3000);
    return () => clearTimeout(t);
  }, [currentUid, guildId, qc]);

  const remove = useMutation({
    mutationFn: () => api<{ deleted: number }>(base, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmDelete(false);
      qc.invalidateQueries({ queryKey: historyKey(guildId) });
    },
  });

  const plays = recent.data?.pages.flatMap((p) => p.plays) ?? [];
  // Day headings: a new one wherever the date changes.
  let lastDay = '';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card className="self-start">
          <CardHeader>
            <CardTitle className="text-base">Zuletzt gespielt</CardTitle>
            <CardDescription>▶ hängt den Song hinten an die Warteschlange. Radio wird nicht mitgezählt.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            {playHint && <p className="pb-2 text-xs text-muted-foreground">{playHint}</p>}
            {recent.isLoading && <p className="text-sm text-muted-foreground">Lade Verlauf…</p>}
            {recent.error && <p className="text-sm text-destructive">{(recent.error as Error).message}</p>}
            {recent.data && plays.length === 0 && (
              <p className="text-sm text-muted-foreground">Noch nichts gespielt — sobald Songs laufen, stehen sie hier.</p>
            )}
            {plays.map((p) => {
              const at = new Date(p.playedAt);
              const day = dayLabel(at);
              const heading = day !== lastDay ? day : null;
              lastDay = day;
              return (
                <div key={p.id}>
                  {heading && (
                    <div className="pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground first:pt-0">{heading}</div>
                  )}
                  <SongRow
                    song={p}
                    meta={[p.artist, p.userName && `von ${p.userName}`, clock(at)].filter(Boolean).join(' · ')}
                    canPlay={canPlay}
                    onPlay={() => onPlay(p.url)}
                    onAddToPlaylist={() => onAddToPlaylist(toSongInput(p))}
                  />
                </div>
              );
            })}
            {recent.hasNextPage && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 w-full"
                disabled={recent.isFetchingNextPage}
                onClick={() => recent.fetchNextPage()}
              >
                {recent.isFetchingNextPage ? 'Lade…' : 'Mehr laden'}
              </Button>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <div role="tablist" className="flex gap-1 rounded-lg border bg-card p-1">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                role="tab"
                aria-selected={range === r.key}
                onClick={() => setRange(r.key)}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  range === r.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          {top.data && (
            <p className="px-1 text-sm text-muted-foreground">
              {top.data.totals.plays} {top.data.totals.plays === 1 ? 'Song' : 'Songs'} gespielt ·{' '}
              {listenTime(top.data.totals.listenedSec)} Musik
            </p>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top-Songs</CardTitle>
              <CardDescription>Zählt, wenn ein Song mindestens 30 Sekunden lief.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1">
              {top.isLoading && <p className="text-sm text-muted-foreground">Lade…</p>}
              {top.error && <p className="text-sm text-destructive">{(top.error as Error).message}</p>}
              {top.data && top.data.songs.length === 0 && <p className="text-sm text-muted-foreground">In diesem Zeitraum nichts.</p>}
              {top.data?.songs.map((s, i) => (
                <div key={s.songKey} className="flex items-center gap-2">
                  <span className="w-6 shrink-0 text-center font-mono text-xs text-muted-foreground">{i + 1}</span>
                  <SongRow
                    song={s}
                    meta={s.artist ?? ''}
                    extra={<span className="shrink-0 font-mono text-xs text-muted-foreground">{s.plays}×</span>}
                    canPlay={canPlay}
                    onPlay={() => onPlay(s.url)}
                    onAddToPlaylist={() => onAddToPlaylist(toSongInput(s))}
                    className="flex-1"
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top-Hörer</CardTitle>
              <CardDescription>Wer sich die meisten Songs gewünscht hat — und wie lange die liefen.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1">
              {top.data && top.data.listeners.length === 0 && <p className="text-sm text-muted-foreground">In diesem Zeitraum niemand.</p>}
              {top.data?.listeners.map((l, i) => (
                <div key={l.userId} className="flex items-center gap-3 rounded-md p-1.5">
                  <span className="w-6 shrink-0 text-center text-sm">{['🥇', '🥈', '🥉'][i] ?? <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>}</span>
                  <Avatar src={l.userAvatar} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{l.userName ?? 'Unbekannt'}</div>
                    <div className="text-xs text-muted-foreground">
                      {l.requests} {l.requests === 1 ? 'Wunsch' : 'Wünsche'} · {listenTime(l.listenedSec)}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
          {confirmDelete ? (
            <>
              <span>Den ganzen Musik-Verlauf dieses Servers löschen?</span>
              <Button variant="destructive" size="sm" disabled={remove.isPending} onClick={() => remove.mutate()}>
                {remove.isPending ? 'Lösche…' : 'Ja, löschen'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                Nein
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" className="gap-1.5 text-destructive" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4" /> Verlauf löschen
            </Button>
          )}
          {remove.error && <span className="text-destructive">{(remove.error as Error).message}</span>}
        </div>
      )}
    </div>
  );
}

function SongRow({
  song,
  meta,
  extra,
  canPlay,
  onPlay,
  onAddToPlaylist,
  className,
}: {
  song: PlayedSong;
  meta: string;
  extra?: ReactNode;
  canPlay: boolean;
  onPlay: () => void;
  onAddToPlaylist: () => void;
  className?: string;
}) {
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => setSent(false), 2000);
    return () => clearTimeout(t);
  }, [sent]);
  return (
    <div className={cn('flex min-w-0 items-center gap-2 rounded-md p-1.5 hover:bg-accent/40 sm:gap-3', className)}>
      <Cover src={song.thumbnail} className="h-9 w-12 sm:w-16" />
      <div className="min-w-0 flex-1">
        <a href={song.url} target="_blank" rel="noreferrer" className="block truncate text-sm hover:text-primary" title={song.title}>
          {song.title}
        </a>
        <div className="truncate text-xs text-muted-foreground">
          {song.source === 'spotify' && <SpotifyBadge />}
          {meta}
        </div>
      </div>
      {extra}
      <IconButton
        label={sent ? 'Angehängt' : 'Hinten anhängen'}
        className="h-8 w-8"
        disabled={!canPlay}
        onClick={() => {
          onPlay();
          setSent(true);
        }}
      >
        {sent ? <Check className="h-4 w-4 text-primary" /> : <Play className="h-4 w-4" />}
      </IconButton>
      <IconButton label="Zur Playlist" className="h-8 w-8" onClick={onAddToPlaylist}>
        <ListPlus className="h-4 w-4" />
      </IconButton>
    </div>
  );
}

function Avatar({ src }: { src: string | null }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className="h-8 w-8 shrink-0 rounded-full" />;
  }
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
      <UserRound className="h-4 w-4" />
    </span>
  );
}
